import { useEffect, useState } from 'react';
import apiClient from '../utils/axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { Zap, TrendingUp, TrendingDown, Activity } from 'lucide-react';

export default function Analytics() {
  const [funnel, setFunnel] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [instantlyOverview, setInstantlyOverview] = useState(null);
  const [instantlyCampaigns, setInstantlyCampaigns] = useState([]);
  const [domainHealth, setDomainHealth] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const [funnelRes, templatesRes, revenueRes, instantlyOverviewRes, instantlyCampaignsRes, domainsRes] = await Promise.all([
        apiClient.get('/analytics/funnel').catch(() => ({ data: null })),
        apiClient.get('/analytics/templates').catch(() => ({ data: { templates: [] } })),
        apiClient.get('/analytics/revenue').catch(() => ({ data: null })),
        apiClient.get('/instantly/overview').catch(() => ({ data: null })),
        apiClient.get('/instantly/campaigns').catch(() => ({ data: { campaigns: [] } })),
        apiClient.get('/instantly/domains').catch(() => ({ data: { domains: [] } }))
      ]);
      setFunnel(funnelRes.data);
      setTemplates(templatesRes.data.templates);
      setRevenue(revenueRes.data);
      setInstantlyOverview(instantlyOverviewRes.data);
      setInstantlyCampaigns(instantlyCampaignsRes.data.campaigns || []);
      setDomainHealth(domainsRes.data.domains || []);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center p-8">Loading analytics...</div>;
  }

  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Analytics</h1>

      {/* Funnel Visualization */}
      {funnel && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Conversion Funnel</h2>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={funnel.funnel}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="stage" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-4">
            {funnel.funnel.map((stage, idx) => (
              <div key={idx} className="text-center">
                <p className="text-sm text-gray-500">{stage.stage}</p>
                <p className="text-2xl font-bold">{stage.count}</p>
                <p className="text-xs text-gray-400">{stage.percentage}%</p>
              </div>
            ))}
          </div>
          <div className="mt-4 text-center">
            <p className="text-lg font-semibold">Total Revenue: ${funnel.revenue?.toFixed(2) || '0.00'}</p>
          </div>
        </div>
      )}

      {/* Template Performance */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Template Performance</h2>
        {templates.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={templates.slice(0, 10)}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="open_rate" fill="#10b981" name="Open Rate %" />
              <Bar dataKey="click_rate" fill="#3b82f6" name="Click Rate %" />
              <Bar dataKey="conversion_rate" fill="#f59e0b" name="Conversion Rate %" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-gray-500 text-center">No template performance data yet</p>
        )}
      </div>

      {/* Revenue Breakdown */}
      {revenue && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Revenue Breakdown</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            {revenue.byGrade.map((item) => (
              <div key={item.grade} className="text-center p-4 bg-gray-50 rounded">
                <p className="text-sm text-gray-500">Grade {item.grade}</p>
                <p className="text-2xl font-bold">{item.count}</p>
                <p className="text-sm text-gray-600">${parseFloat(item.revenue || 0).toFixed(2)}</p>
              </div>
            ))}
          </div>
          <div className="text-center pt-4 border-t">
            <p className="text-lg font-semibold">Total MQLs: {revenue.total.mqls}</p>
            <p className="text-2xl font-bold text-green-600">Total Revenue: ${parseFloat(revenue.total.revenue || 0).toFixed(2)}</p>
          </div>
        </div>
      )}

      {/* Instantly Overview */}
      {instantlyOverview && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Zap className="text-blue-600" size={24} />
            <h2 className="text-xl font-semibold">Instantly Integration</h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-sm text-gray-500 mb-1">Active Campaigns</div>
              <div className="text-2xl font-bold text-blue-600">{instantlyOverview.campaigns?.active || 0}</div>
              <div className="text-xs text-gray-500 mt-1">of {instantlyOverview.campaigns?.total || 0} total</div>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-sm text-gray-500 mb-1">Healthy Domains</div>
              <div className="text-2xl font-bold text-green-600">{instantlyOverview.domains?.healthy || 0}</div>
              <div className="text-xs text-gray-500 mt-1">of {instantlyOverview.domains?.total || 0} total</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="text-sm text-gray-500 mb-1">Active Warmup</div>
              <div className="text-2xl font-bold text-purple-600">{instantlyOverview.domains?.activeWarmup || 0}</div>
              <div className="text-xs text-gray-500 mt-1">domains warming up</div>
            </div>
            <div className="bg-yellow-50 rounded-lg p-4">
              <div className="text-sm text-gray-500 mb-1">Emails Sent Today</div>
              <div className="text-2xl font-bold text-yellow-600">{instantlyOverview.domains?.totalEmailsSentToday || 0}</div>
              <div className="text-xs text-gray-500 mt-1">across all domains</div>
            </div>
          </div>

          {/* Domain Health Chart */}
          {domainHealth.length > 0 && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold mb-4">Domain Health Distribution</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={domainHealth.slice(0, 10)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="domain_name" angle={-45} textAnchor="end" height={100} />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="health_score" fill="#3b82f6" name="Health Score" />
                  <Bar dataKey="inbox_placement_rate" fill="#10b981" name="Inbox Placement %" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* Instantly Campaign Performance */}
      {instantlyCampaigns.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Instantly Campaign Performance</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campaign</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sends</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Opens</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Clicks</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Replies</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bounces</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Open Rate</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Click Rate</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {instantlyCampaigns.map((campaign) => (
                  <tr key={campaign.id}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{campaign.name}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        campaign.instantly_status === 'active' ? 'bg-green-100 text-green-800' :
                        campaign.instantly_status === 'paused' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {campaign.instantly_status || 'unknown'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">{campaign.sends_count || 0}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{campaign.opens_count || 0}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{campaign.clicks_count || 0}</td>
                    <td className="px-4 py-3 text-sm text-green-600">{campaign.replies_count || 0}</td>
                    <td className="px-4 py-3 text-sm text-red-600">{campaign.bounces_count || 0}</td>
                    <td className="px-4 py-3 text-sm">
                      {campaign.open_rate ? `${parseFloat(campaign.open_rate).toFixed(1)}%` : 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {campaign.click_rate ? `${parseFloat(campaign.click_rate).toFixed(1)}%` : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
